import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useKeycloak } from "@react-keycloak/web";
import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import StatusPill from "../Budgets/components/StatusPill.jsx";
import Badge from "../../components/ui-components/Badge.jsx";
import Modal from "../../components/ui-components/Modal.jsx";
import { toast } from "../../components/ui-components/Toast.jsx";
import { primaryButtonCls, secondaryButtonCls } from "../../components/ui-components/buttonStyles.js";
import BoardReportPreview from "./BoardReportPreview.jsx";
import BoardReportInspector from "./inspector/BoardReportInspector.jsx";
import useReportMetrics from "./metrics/useReportMetrics.js";
import { METRIC_CATALOG } from "./metrics/reportMetrics.js";
import {
  getBoardReportRun,
  updateRunNarrative,
  updateRunDrivers,
  approveRun,
} from "./document/boardReportApi.js";

// Run lifecycle status -> Badge display label + the STATUS_TONE key (in
// Badge.jsx) whose color best fits that status. "in_review" borrows the
// "awaiting" key for its warning tone since Badge has no native mapping for
// it; "draft" borrows the "pending" key for the same reason (both resolve to
// Badge's neutral tone). Every value here must be a real STATUS_TONE key —
// an unmapped key silently (and misleadingly) falls back to neutral.
const RUN_STATUS_LABEL = { draft: "Draft", in_review: "In review", approved: "Approved" };
const RUN_STATUS_TONE_KEY = { draft: "pending", in_review: "awaiting", approved: "approved" };

const emptyNarrative = () => ({
  status: "",
  bullets: [],
  what_changed: "",
  why: "",
  what_to_do: "",
  provisional: false,
  overview_summary: "",
  performance_summary: "",
  drivers_summary: "",
  forecast_summary: "",
  decision_required: "",
  decision_owner: "",
  decision_deadline: "",
});

/**
 * BoardReportReview - the narrative review/approve screen for a single board
 * report run (/board-reports/:id/runs/:runId/review).
 *
 * Loads the run detail (Task 11's GET .../runs/:runId), lets a reviewer edit
 * the LLM-drafted narrative (bullets + what_changed/why/what_to_do, the four
 * per-page summaries, and the author-entered decision_required/owner/deadline
 * block - all snake_case per the backend contract and all part of the same
 * Narrative struct), save the edits (PUT .../narrative, which flips a draft
 * run to in_review), edit the run's driver annotations (Task 8's separate
 * PUT .../drivers - cause/expected/owner/action are author-editable, driver/
 * impact/category are seeded read-only (category is SP2 Task 4's computed
 * Volume/Rate classification, shown as a Badge); unlike the narrative PUT this does NOT flip
 * draft -> in_review), preview the PDF, and approve the run (POST .../approve,
 * a one-way freeze mirrored here by disabling every field - narrative,
 * summaries, decision, and drivers - once run.status === "approved").
 *
 * SP6 Task 6 redesign: this screen is now report-canvas (primary) + contextual inspector
 * (secondary), replacing SP5's fixed narrative-rail + preview two-pane layout:
 *   - The report canvas IS the editor - `BoardReportPreview` renders `selectable`, so clicking
 *     any block on the live page calls `handleSelectBlock(blockId, blockType, binding)`, which
 *     becomes this screen's single source of truth for "what am I editing right now"
 *     (`selectedBlockId`/`selectedBlockType`/`selectedBinding`).
 *   - `BoardReportInspector` renders whichever field editors (or read-only data panel) apply to
 *     the selected block (see `inspector/blockInspector.js`), reusing the field components the old
 *     `NarrativeRail` used (relocated to `inspector/fields/*`). `NarrativeRail`/`narrativeFieldPage`
 *     themselves were DELETED in SP6 Task 7 - nothing in the app imported either file any more (only
 *     their own tests did), so the retirement started in Task 6 is now complete; their field-editor
 *     behavior and focus-scroll live on via `inspector/fields/*` and block selection, respectively.
 *   - `useReportMetrics(id, runId)` is called here (react-query-cached via `useBlockData`) so the
 *     inspector's "Source data" panels and `{{fig:KEY}}` token resolution share the exact cache
 *     entries `BoardReportPages` (rendered inside the preview) already populates - no extra
 *     network round trip for the same figures.
 *   - The live-reflection behavior SP5 introduced is unchanged in spirit: every keystroke in an
 *     inspector field still flows straight into `narrative`/`drivers` state, which the report
 *     canvas re-renders via `narrativeOverride`/`driversOverride` - just routed through the
 *     inspector's callbacks instead of the rail's.
 *   - The old field->page scroll (`focusPageId` via `narrativeFieldPage`) doesn't carry over:
 *     block selection now IS the navigation model, so `onFieldFocus` is a no-op here (kept only
 *     because `BoardReportInspector`'s field components still report focus for a future use).
 *   - The Save/Approve/Save-drivers action bar is persistent - always visible above the
 *     canvas+inspector row (not tucked inside either pane) so it survives whatever's selected or
 *     scrolled, matching how NarrativeRail's buttons were always reachable before. A narrow-screen
 *     ("<lg") reader sees the inspector as a bottom sheet instead of a side panel, with its own
 *     "Close" affordance (`handleCloseInspector`) since nothing else on that layout dismisses it.
 *
 * SP6 Task 7 notes (final gate + deferred scope):
 *   - `{{fig:KEY}}` @figures resolve LIVE at render time (`renderNarrative.jsx`, fed by
 *     `useReportMetrics`) - for the exported PDF this means the value is whatever the figure
 *     endpoints return at chromedp-render time, then BAKED into the frozen PDF artifact (the PDF is
 *     a point-in-time render, not a live document - a later correction to the underlying figures
 *     does not retroactively change an already-generated PDF).
 *   - The metric catalog (`METRIC_CATALOG`, reportMetrics.js) covers ~13 core metrics across the 5
 *     figure endpoints; it's deliberately extensible, not exhaustive. `prev_month_total` is DERIVED
 *     (not a direct field on any figure response). `breach_prob` was scoped in the SP6 plan but
 *     dropped from the catalog - no figure endpoint exposes a modeled breach probability today; it
 *     is deferred pending a financial-control-summary metric source that would carry one.
 *   - The token editor (`NarrativeTokenField`/`MetricPicker`) is `@`-mentions + plain text only - no
 *     rich formatting (bold/links/etc.), matching the SP6 plan's explicit scope.
 *   - `useReportMetrics` fetches all 5 figure endpoints on EVERY render of this screen (and of
 *     `BoardReportPages`, including the print/PDF path) regardless of whether the run's narrative
 *     actually contains any `{{fig:KEY}}` tokens - react-query's cache keeps this cheap for repeat
 *     renders of an already-mounted screen, but the PDF-export path mounts fresh each time, so this
 *     is a standing PDF-generation-latency note (5 endpoint round trips per export, token-free runs
 *     included) rather than a functional gap.
 *   - Standing deferreds (recorded, not scheduled): interactive charts (hover/drill) on the report
 *     canvas; a broader/scoped-dimension metric catalog beyond the ~13 core keys; and a live-browser
 *     Playwright visual smoke test of the report canvas + contextual inspector + `@figure` rendering
 *     BOTH in-app and in the actual generated PDF - this remains a human-run smoke test, not
 *     automated in this suite.
 */
const BoardReportReview = () => {
  const { id, runId } = useParams();
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();

  const [run, setRun] = useState(null);
  const [narrative, setNarrative] = useState(emptyNarrative());
  const [drivers, setDrivers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  // SP6 Task 6 selection state: which block on the report canvas is currently open in the
  // contextual inspector. Set by BoardReportPreview's onSelectBlock (the ManifestPage selection
  // seam - see BoardReportPages.jsx/ManifestPage.jsx), read by BoardReportInspector to resolve
  // which field editors (or read-only data panel) to show (blockInspector.js's inspectorFor()).
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [selectedBlockType, setSelectedBlockType] = useState(null);
  const [selectedBinding, setSelectedBinding] = useState(null);

  const handleSelectBlock = useCallback((blockId, blockType, binding) => {
    setSelectedBlockId(blockId);
    setSelectedBlockType(blockType);
    setSelectedBinding(binding);
  }, []);

  // handleCloseInspector - clears the selection so the narrow-screen inspector sheet (a fixed
  // bottom sheet on <lg, see the layout below) has an explicit, accessible way to dismiss: without
  // this, selecting any block on a narrow viewport would leave the sheet permanently covering part
  // of the canvas with no way back except selecting yet another block. On >=lg screens the
  // inspector is an always-present side panel, so this just falls back to its own empty state.
  const handleCloseInspector = useCallback(() => {
    setSelectedBlockId(null);
    setSelectedBlockType(null);
    setSelectedBinding(null);
  }, []);

  // Shared with BoardReportPages (rendered inside BoardReportPreview) via react-query's cache -
  // no extra network traffic for the same figures. Feeds the inspector's "Source data" panels and
  // @figure token resolution.
  const { metrics } = useReportMetrics(id, runId);

  const loadRun = useCallback(async () => {
    if (!keycloak?.token || !id || !runId) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setLoadError(null);
      const detail = await getBoardReportRun(id, runId);
      setRun(detail.run);
      setNarrative({ ...emptyNarrative(), ...(detail.run?.narrative || {}) });
      setDrivers(detail.run?.drivers || []);
    } catch (err) {
      setLoadError(err.message || "Failed to load run");
    } finally {
      setIsLoading(false);
    }
  }, [keycloak?.token, id, runId]);

  useEffect(() => {
    loadRun();
  }, [loadRun]);

  const isApproved = run?.status === "approved";
  const isBusy = isSaving || isApproving;
  const isReadOnly = isApproved || isBusy;

  const setField = (field, value) => setNarrative((n) => ({ ...n, [field]: value }));

  const handleBulletChange = (index, value) =>
    setNarrative((n) => ({ ...n, bullets: n.bullets.map((b, i) => (i === index ? value : b)) }));

  const handleAddBullet = () => setNarrative((n) => ({ ...n, bullets: [...n.bullets, ""] }));

  const handleRemoveBullet = (index) =>
    setNarrative((n) => ({ ...n, bullets: n.bullets.filter((_, i) => i !== index) }));

  // persistEdits - saves BOTH editable surfaces of a run: the narrative (PUT .../narrative, which
  // flips a draft to in_review) and the driver annotations (PUT .../drivers). Returns the latest run
  // from the driver response (it reflects both writes), or throws so callers can surface the error.
  // A single "Save" persisting everything replaces the old separate Save / Save-drivers buttons - the
  // narrative/drivers split is a backend implementation detail no reviewer should have to track.
  const persistEdits = async () => {
    await updateRunNarrative(id, runId, narrative);
    return updateRunDrivers(id, runId, drivers);
  };

  const applyUpdatedRun = (updated) => {
    if (!updated) return;
    setRun(updated);
    setNarrative({ ...emptyNarrative(), ...(updated.narrative || {}) });
    setDrivers(updated.drivers || []);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      applyUpdatedRun(await persistEdits());
      toast.success("Saved");
    } catch (err) {
      if (err?.status === 409) {
        toast.error("This run can no longer be edited - refreshing.");
        loadRun();
      } else {
        toast.error("Failed to save");
      }
    } finally {
      setIsSaving(false);
    }
  };

  // handleApprove - saves the current edits FIRST (so you never lock a stale draft), then approves.
  // Confirmed via a modal because approval is a one-way freeze. On 409 (someone else advanced the
  // run) we refresh rather than silently fail.
  const handleApprove = async () => {
    setShowApproveConfirm(false);
    setIsApproving(true);
    try {
      await persistEdits();
      applyUpdatedRun(await approveRun(id, runId));
      toast.success("Run approved & locked");
    } catch (err) {
      if (err?.status === 409) {
        toast.error("This run can no longer be approved - refreshing.");
        loadRun();
      } else {
        toast.error("Failed to approve run");
      }
    } finally {
      setIsApproving(false);
    }
  };

  const handlePreviewPdf = () => {
    window.open(`/board-report-print/${id}/${runId}?print=true`, "_blank", "noopener,noreferrer");
  };

  const handleDriverFieldChange = (index, field, value) =>
    setDrivers((ds) => ds.map((d, i) => (i === index ? { ...d, [field]: value } : d)));

  if (isLoading) {
    return (
      <div className="h-full flex flex-col bg-background-grey">
        <AdministrationHeader title="Review board report" showPeriodSelector={false} />
        <div className="p-8 text-text-3">Loading…</div>
      </div>
    );
  }

  if (loadError || !run) {
    return (
      <div className="h-full flex flex-col bg-background-grey">
        <AdministrationHeader title="Review board report" showPeriodSelector={false} />
        <div className="p-8 text-danger">
          Failed to load run{loadError ? `: ${loadError}` : "."}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background-grey">
      <AdministrationHeader title="Review board report" showPeriodSelector={false} />

      <div className="flex-1 overflow-hidden flex flex-col p-8 pb-0 gap-4">
        <button
          type="button"
          className="text-sm text-text-3 hover:text-text inline-flex items-center gap-1 cursor-pointer self-start"
          onClick={() => navigate(`/board-reports/${id}?tab=runs`)}
        >
          <span aria-hidden="true">←</span> Back to runs
        </button>

        {/* Persistent action bar (SP6 Task 6): always visible above the canvas+inspector row so
            Save/Approve stay reachable no matter what block is selected or how far either pane has
            scrolled. The two status chips are labelled - one is the budget position, the other the
            review workflow state - so they don't read as one thing. */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] font-medium uppercase tracking-wide text-text-3">Budget</span>
            <StatusPill status={narrative.status} />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] font-medium uppercase tracking-wide text-text-3">Status</span>
            <Badge
              status={RUN_STATUS_TONE_KEY[run.status] || run.status}
              label={RUN_STATUS_LABEL[run.status] || run.status}
            />
          </div>
          {narrative.provisional && (
            <span className="text-xs text-warning italic">provisional (LLM unavailable)</span>
          )}
          <div className="ml-auto flex items-center gap-3">
            <button
              type="button"
              className={secondaryButtonCls}
              onClick={handlePreviewPdf}
              aria-label="Preview PDF"
            >
              Preview PDF
            </button>
            <button
              type="button"
              className={secondaryButtonCls}
              onClick={handleSave}
              disabled={isReadOnly}
            >
              {isSaving ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              className={primaryButtonCls}
              onClick={() => setShowApproveConfirm(true)}
              disabled={isReadOnly}
            >
              {isApproving ? "Approving…" : "Approve & lock"}
            </button>
          </div>
        </div>

        <Modal
          show={showApproveConfirm}
          close={() => setShowApproveConfirm(false)}
          alertIcon
          title="Approve & lock this run?"
          description={
            <span>
              This saves your edits and locks{run?.version ? ` v${run.version}` : " this run"} for
              board sign-off. A locked run can&apos;t be edited afterward — generate a new run if
              something needs to change.
            </span>
          }
          confirmButton="Approve & lock"
          cancelButton="Cancel"
          confirmAction={handleApprove}
        />

        {isApproved && (
          <div className="px-4 py-3 bg-success-soft border border-success/30 rounded-card text-sm text-text">
            This run is approved and locked. Narrative fields are read-only.
          </div>
        )}

        {/* Report-canvas (primary) + contextual inspector (secondary) - SP6 Task 6. The canvas
            is the live, selectable report itself; clicking a block opens its editor (or
            read-only data panel) in the inspector alongside it. On `lg`+ screens the inspector is
            a fixed-width right panel, always present (its own empty state covers "nothing
            selected yet"). Below `lg` it becomes a bottom slide-over that only appears once a
            block is selected, so the canvas gets the full narrow viewport until then. */}
        {/* Fixed, viewport-bounded height (NOT flex-1): the app shell is min-h-screen (content-driven
            height), so a flex-1 pane never bounds to the viewport — it would grow to the full report
            height (~7000px), the page (not the canvas) would scroll, and the reader would land on the
            blank cover with the inspector scrolling away. Bounding the row to the viewport makes the
            report canvas scroll INTERNALLY and keeps the inspector pinned beside it — the mockup's
            bounded-pane behavior. 160px ≈ the app top bar + this screen's action bar + paddings. */}
        <div className="h-[calc(100vh-160px)] min-h-[520px] flex flex-col lg:flex-row gap-6 pb-4 relative">
          <div className="flex-1 min-w-0 min-h-0 border border-card-bd rounded-card overflow-hidden bg-card">
            <BoardReportPreview
              id={id}
              runId={runId}
              embedded
              chromeless
              selectable
              selectedBlockId={selectedBlockId}
              selectedBinding={selectedBinding}
              onSelectBlock={handleSelectBlock}
              narrativeOverride={narrative}
              driversOverride={drivers}
            />
          </div>

          <div
            /* NOTE: `w-*` Tailwind utilities are no-ops in this project (the theme.width scale is
               overridden — see reference_tailwind-width-height-noop), so the fixed panel width MUST
               come from min-w/max-w (which do work) + grow-0, not `w-96`. Without this the panel
               shrink-wraps to its content: narrow when empty, but FULL-WIDTH once a block's wide
               editor is shown — which squeezes the report canvas to nothing (the bug that made the
               screen not match the mockup). */
            className={`
              ${selectedBlockId ? "block" : "hidden"} lg:block
              fixed inset-x-0 bottom-0 z-20 max-h-[70vh] overflow-auto
              lg:static lg:min-w-[360px] lg:max-w-[360px] lg:grow-0 lg:shrink-0 lg:max-h-none lg:overflow-auto
            `}
          >
            {/* Narrow-screen-only close affordance for the bottom-sheet inspector (a11y sweep,
                Task 7): the sheet has no other way to dismiss on <lg once a block is selected. On
                lg+ the inspector is a permanent side panel, so this is hidden there. */}
            {selectedBlockId && (
              <button
                type="button"
                className="lg:hidden w-full flex justify-center py-2 text-sm text-text-3 bg-card border-b border-card-bd sticky top-0"
                onClick={handleCloseInspector}
                aria-label="Close inspector"
              >
                Close
              </button>
            )}
            <BoardReportInspector
              selectedBlockId={selectedBlockId}
              selectedBlockType={selectedBlockType}
              selectedBinding={selectedBinding}
              narrative={narrative}
              drivers={drivers}
              metrics={metrics}
              catalog={METRIC_CATALOG}
              isReadOnly={isReadOnly}
              boardReportId={id}
              runId={runId}
              onNarrativeField={setField}
              onBulletChange={handleBulletChange}
              onAddBullet={handleAddBullet}
              onRemoveBullet={handleRemoveBullet}
              onDriverFieldChange={handleDriverFieldChange}
              onFieldFocus={() => {}}
              onSelectBlock={handleSelectBlock}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoardReportReview;
